/**
 * Onboarding welcome dialog.
 *
 * A simple centered, scrim-backed splash dialog rendered on the
 * workspaces / home index page when the user hasn't yet completed
 * onboarding. It re-uses the
 * existing org onboarding API (`/api/v1/org/:organization_id/onboarding`)
 * via `useUserOnboarding` to read status and the matching mutation hooks
 * to mark progress / dismissal — so this dialog stays in sync with the
 * server-side onboarding state used elsewhere in the app.
 *
 * Visual language is borrowed from the canvas onboarding-welcome system
 * (`pages/canvas/nodes/widgets/onboarding-welcome`):
 *   • Shared `_co_onb` design tokens (paper / ink / rust palette, settle
 *     entrance staircase, eyebrow dot pulse, rust pill CTA).
 *   • `StepHeader`-style hierarchy with a mono-uppercase eyebrow, an
 *     Instrument Serif italic flourish in the heading, and a quiet lede.
 *
 * Interaction model:
 *   • Primary CTA creates a new canvas in the user's private workspace
 *     and routes to it. Onboarding global status is set to "in_progress"
 *     so the dialog won't reopen on subsequent home visits in this org.
 *   • "Maybe later" sets the global status to "skipped" and dismisses.
 *   • The dialog is non-dismissible by ESC or scrim-click — the user
 *     must choose one of the two explicit actions. This stops an
 *     accidental outside-click from skipping past onboarding. Focus is
 *     trapped to the dialog while open and restored to the
 *     previously-focused element on close.
 */

import { useCreateCanvas } from "@/api/canvas";
import {
  useUpdateGlobalOnboardingStatus,
  useUserOnboarding,
} from "@/api/onboarding";
import { useUserInfo } from "@/api/user";
import { ROUTES } from "@/constants/routes";
import { ONBOARDING_STYLES } from "@/pages/canvas/nodes/widgets/onboarding-welcome/styles";
import { Flags } from "@/providers/LaunchDarklyProvider";
import {
  trackOnboardingWelcomeSkipped,
  trackOnboardingWelcomeStarted,
} from "@/utils/analytics";
import { route } from "@/utils/route";
import { useThemeContext } from "@cohesive/ui-components";
import { useFlags } from "launchdarkly-react-client-sdk";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const OnboardingWelcomeDialog = memo(function OnboardingWelcomeDialog({
  suppressed = false,
}: { suppressed?: boolean } = {}) {
  const navigate = useNavigate();
  const { mode } = useThemeContext();
  const { data: userInfo } = useUserInfo();
  const { data: onboarding, isLoading: onboardingLoading } = useUserOnboarding({
    enabled: true,
  });
  const { setInProgress, setSkipped } = useUpdateGlobalOnboardingStatus();
  const flags = useFlags<Flags>();

  /**
   * Locally suppress re-opening within this session if the user dismisses
   * before the network round-trip lands (`useUpdateGlobalOnboardingStatus`
   * is fire-and-forget). Without this the dialog would briefly flicker
   * back in until the cache update settles.
   */
  const [locallyDismissed, setLocallyDismissed] = useState(false);

  // Only the initial (never-started) state shows the full-screen
  // dialog. Once the user has skipped, started ("in_progress"), or
  // completed onboarding, the popover would be intrusive on a revisit —
  // skipped users instead get the "Finish onboarding" card on the home
  // page's Recents row.
  const globalStatus = onboarding?.global ?? "";
  const shouldShow =
    // The template-copy prompt owns the first home view after a template
    // signup. Suppression is view-local — onboarding's server state is
    // untouched, so these gates run normally on the next home view.
    !suppressed &&
    !!flags.onboarding &&
    !onboardingLoading &&
    !locallyDismissed &&
    !!onboarding &&
    globalStatus !== "complete" &&
    globalStatus !== "skipped" &&
    globalStatus !== "in_progress";

  const createCanvas = useCreateCanvas({
    onSuccess: (canvas) => {
      /**
       * `seedOnboardingWidget` is read by `useSeedOnboardingWidget` on
       * the canvas page once Loro is ready — it inserts the invisible
       * onboarding-welcome widget shell so the screen-docked overlay
       * starts the step sequence on this fresh canvas.
       */
      navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: canvas.id }), {
        state: { seedOnboardingWidget: true },
      });
    },
    onError: () => {
      toast.error("Couldn't open a canvas. Please try again.");
    },
  });

  const handleStart = useCallback(() => {
    if (createCanvas.isPending) return;
    trackOnboardingWelcomeStarted();
    setInProgress();
    createCanvas.mutate({});
  }, [createCanvas, setInProgress]);

  const handleSkip = useCallback(() => {
    trackOnboardingWelcomeSkipped();
    setLocallyDismissed(true);
    setSkipped();
  }, [setSkipped]);

  const isPending = createCanvas.isPending;

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!shouldShow) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const node = dialogRef.current;
    if (node) {
      const first = node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      // The dialog is intentionally non-dismissible — Escape does
      // nothing. The user must pick a canvas or "Maybe later".
      if (e.key === "Tab" && node) {
        const focusables = Array.from(
          node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((el) => !el.hasAttribute("data-co-onb-trap"));
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      previouslyFocusedRef.current?.focus?.();
    };
  }, [shouldShow]);

  const firstName = userInfo?.user?.first_name?.trim() || "";

  const heading = useMemo(
    () => (
      <>
        A visual canvas for all your <Flourish>context</Flourish>.
      </>
    ),
    [],
  );

  if (!shouldShow) return null;

  return createPortal(
    <div className={mode === "dark" ? "dark" : undefined}>
      <style>{ONBOARDING_STYLES}</style>
      <style>{DIALOG_STYLES}</style>
      {/* Scrim is intentionally inert — clicking it does not dismiss the
          dialog. The user must pick a canvas or "Maybe later". */}
      <div className="_co_onb _co_home_onb_scrim" role="presentation">
        <div
          ref={dialogRef}
          className="_co_onb _co_home_onb_card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="_co_home_onb_heading"
          aria-describedby="_co_home_onb_lede"
        >
          <div className="_co_home_onb_content">
            <div
              className="_co_onb_settle_1 _co_home_onb_eyebrow"
              aria-hidden={false}
            >
              <span
                className="_co_onb_dot"
                style={{
                  width: "var(--co-onb-dot)",
                  height: "var(--co-onb-dot)",
                  borderRadius: "50%",
                  background: "var(--co-rust)",
                  display: "inline-block",
                }}
              />
              {firstName ? `Welcome, ${firstName}` : "Welcome to Cohesive"}
            </div>

            <h1
              id="_co_home_onb_heading"
              className="_co_onb_settle_2 _co_home_onb_heading"
            >
              {heading}
            </h1>

            <p
              id="_co_home_onb_lede"
              className="_co_onb_settle_3 _co_home_onb_lede"
            >
              Cohesive transforms conversations, files, ideas, and workflows
              into a shared spatial workspace for you and your AI agents. Open
              your first canvas for a guided tour.
            </p>

            <div className="_co_onb_settle_4 _co_home_onb_actions">
              <button
                type="button"
                className="_co_onb_finish _co_home_onb_primary"
                // `alt` isn't a typed <button> prop but renders to the
                // DOM and is captured by Sentry replay click selectors —
                // used to filter for sessions that started onboarding.
                {...({ alt: "onboarding-create-first-canvas" } as Record<
                  string,
                  string
                >)}
                onClick={handleStart}
                disabled={isPending}
                autoFocus
              >
                {isPending ? (
                  <>
                    <Spinner />
                    Opening canvas…
                  </>
                ) : (
                  <>
                    Open your first canvas
                    <Arrow />
                  </>
                )}
              </button>

              <button
                type="button"
                className="_co_home_onb_secondary"
                // `alt` is surfaced in Sentry replay click selectors.
                {...({ alt: "onboarding-maybe-later" } as Record<
                  string,
                  string
                >)}
                onClick={handleSkip}
                disabled={isPending}
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
});

const Flourish = memo(function Flourish({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <span
      style={{
        fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Georgia', serif",
        fontStyle: "italic",
        fontWeight: 400,
        letterSpacing: "-0.01em",
        color: "var(--co-fg)",
      }}
    >
      {children}
    </span>
  );
});

const Arrow = memo(function Arrow() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
      style={{ transform: "translateY(0.5px)" }}
    >
      <path
        d="M3 7h8m0 0L7.5 3.5M11 7l-3.5 3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
});

const Spinner = memo(function Spinner() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
      className="_co_home_onb_spinner"
    >
      <circle
        cx="7"
        cy="7"
        r="5.25"
        stroke="currentColor"
        strokeOpacity="0.3"
        strokeWidth="1.6"
      />
      <path
        d="M12.25 7a5.25 5.25 0 0 0-5.25-5.25"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
});

const DIALOG_STYLES = `
  ._co_home_onb_scrim {
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: rgba(15, 12, 8, 0.42);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    animation: _co_home_onb_scrim_in 240ms cubic-bezier(0.2, 0, 0, 1) both;
  }
  .dark ._co_home_onb_scrim {
    background: rgba(0, 0, 0, 0.62);
  }
  @keyframes _co_home_onb_scrim_in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  ._co_home_onb_card {
    /* Splash variant tokens — same scale as the centered onboarding
       splash card so typography and stack rhythm match exactly. */
    --co-onb-fs-eyebrow: 12px;
    --co-onb-fs-heading: 38px;
    --co-onb-fs-heading-display: 56px;
    --co-onb-fs-lede: 17px;
    --co-onb-fs-hint: 12px;
    --co-onb-fs-meta: 16px;
    --co-onb-fs-cta: 14px;
    --co-onb-stack-xs: 6px;
    --co-onb-stack-sm: 12px;
    --co-onb-stack-md: 20px;
    --co-onb-stack-lg: 32px;
    --co-onb-stack-xl: 44px;
    --co-onb-pad-x: 40px;
    --co-onb-pad-y: 36px;
    --co-onb-content-max: 520px;
    --co-onb-dot: 9px;

    position: relative;
    width: min(560px, 100%);
    max-height: calc(100vh - 48px);
    overflow: hidden;
    background: var(--co-paper);
    color: var(--co-fg);
    border: 1px solid var(--co-border);
    border-radius: 22px;
    box-shadow:
      0 1px 2px rgba(15, 12, 8, 0.06),
      0 28px 80px rgba(15, 12, 8, 0.28);
    font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Inter', 'Geist', sans-serif;
    animation: _co_home_onb_card_in 420ms cubic-bezier(0.2, 0, 0, 1) both;
  }
  .dark ._co_home_onb_card {
    background: rgba(26, 26, 22, 0.98);
    box-shadow:
      0 1px 2px rgba(0, 0, 0, 0.4),
      0 32px 96px rgba(0, 0, 0, 0.6);
  }
  @keyframes _co_home_onb_card_in {
    from { opacity: 0; transform: translateY(10px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0)    scale(1); }
  }

  ._co_home_onb_content {
    padding: var(--co-onb-pad-y) var(--co-onb-pad-x);
    display: flex;
    flex-direction: column;
    gap: var(--co-onb-stack-md);
    align-items: stretch;
    text-align: left;
  }

  ._co_home_onb_eyebrow {
    font-family: ui-monospace, 'JetBrains Mono', 'SF Mono', Menlo, monospace;
    font-size: var(--co-onb-fs-eyebrow);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--co-fg-muted);
    display: inline-flex;
    align-items: center;
    gap: var(--co-onb-stack-sm);
  }

  ._co_home_onb_heading {
    margin: 0;
    max-width: var(--co-onb-content-max);
    font-size: var(--co-onb-fs-heading);
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 500;
    color: var(--co-fg);
  }

  ._co_home_onb_lede {
    margin: 0;
    max-width: var(--co-onb-content-max);
    font-size: var(--co-onb-fs-lede);
    line-height: 1.5;
    color: var(--co-fg-muted);
    font-weight: 400;
  }

  ._co_home_onb_actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px;
    margin-top: var(--co-onb-stack-sm);
  }

  ._co_home_onb_primary[disabled] {
    opacity: 0.7;
    cursor: progress;
    scale: 1 !important;
  }

  ._co_home_onb_secondary {
    appearance: none;
    background: transparent;
    border: none;
    padding: 0 8px;
    margin: 0;
    min-height: 44px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    color: var(--co-fg-muted);
    cursor: pointer;
    border-radius: 8px;
    transition: color 160ms cubic-bezier(0.2, 0, 0, 1);
  }
  ._co_home_onb_secondary:hover { color: var(--co-fg); }
  ._co_home_onb_secondary:focus-visible {
    outline: 2px solid var(--co-rust);
    outline-offset: 2px;
  }
  ._co_home_onb_secondary[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @keyframes _co_home_onb_spin {
    to { transform: rotate(360deg); }
  }
  ._co_home_onb_spinner {
    animation: _co_home_onb_spin 700ms linear infinite;
    transform-origin: 50% 50%;
  }

  @media (max-width: 520px) {
    ._co_home_onb_card {
      --co-onb-fs-heading: 30px;
      --co-onb-pad-x: 24px;
      --co-onb-pad-y: 28px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    ._co_home_onb_scrim,
    ._co_home_onb_card,
    ._co_home_onb_spinner { animation: none !important; }
  }
`;
